import { useQuery } from '@tanstack/vue-query';
import { useExperienceApi } from '@/shared/api/useExperienceApi.ts';
import { computed, type ComputedRef, type MaybeRefOrGetter, unref } from 'vue';
import { createExperienceAggregationsFromResource } from '@/entities/experience/aggregation/model/create-experience-aggregations-from-resource.ts';
import type { IExperienceAggregations } from '@/entities/experience/aggregation/model/types.ts';
import type { TMovementType } from '@/entities/movement-type';
import { useUnlicensedContentFilter } from '@/shared/composables/useUnlicensedContentFilter.ts';
import { useLoaderStore } from '@/shared/store/useLoaderStore';
import type { UseExperienceAggregationsDisplayCurrencyOptions } from '@/shared/types/displayCurrency';
import { useAggregationDisplayCurrencyQueryParams } from '@/entities/experience/aggregation/lib/useAggregationDisplayCurrencyQueryParams';

export interface Params {
  city__slug?: MaybeRefOrGetter<string | null>;
  region__slug?: MaybeRefOrGetter<string | null>;
  country__slug?: MaybeRefOrGetter<string | null>;
  type?: MaybeRefOrGetter<string>;
  start_price?: MaybeRefOrGetter<number | null>;
  end_price?: MaybeRefOrGetter<number | null>;
  start_duration?: MaybeRefOrGetter<number | null>;
  end_duration?: MaybeRefOrGetter<number | null>;
  start_date?: MaybeRefOrGetter<string | null>;
  end_date?: MaybeRefOrGetter<string | null>;
  movement_type?: MaybeRefOrGetter<string | null>;
  tour_activity_slugs?: MaybeRefOrGetter<string | null>;
  persons_count?: MaybeRefOrGetter<number>;
  page?: MaybeRefOrGetter<number>;
  tag__slugs?: MaybeRefOrGetter<string | null>;
  sorting?: MaybeRefOrGetter<string | null>;
  date_filter_mode?: MaybeRefOrGetter<string>;
  bookable?: MaybeRefOrGetter<boolean>;
  start_time_interval?: MaybeRefOrGetter<string>;
  comfort_type?: MaybeRefOrGetter<string>;
  accommodation_type?: MaybeRefOrGetter<string>;
  activity_level_category?: MaybeRefOrGetter<string>;
  duration_interval?: MaybeRefOrGetter<string>;
  price_format?: MaybeRefOrGetter<string>;
  children_allowed?: MaybeRefOrGetter<boolean | undefined>;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const normalizeParams = (params: Params) => {
  return {
    city__slug: params?.city__slug ? (unref(params?.city__slug) as string) : undefined,
    region__slug: params?.region__slug ? (unref(params?.region__slug) as string) : undefined,
    country__slug: params?.country__slug ? (unref(params?.country__slug) as string) : undefined,
    type: params?.type ? (unref(params?.type) as string) : undefined,
    start_date: params?.start_date ? (unref(params?.start_date) as string) : undefined,
    end_date: params?.end_date ? (unref(params?.end_date) as string) : undefined,
    movement_type: params?.movement_type ? (unref(params?.movement_type) as string) : undefined,
    tour_activity_slugs: params?.tour_activity_slugs ? (unref(params?.tour_activity_slugs) as string) : undefined,
    start_price: params?.start_price ? (unref(params?.start_price) as number) : undefined,
    end_price: params?.end_price ? (unref(params?.end_price) as number) : undefined,
    start_duration: params?.start_duration ? (unref(params?.start_duration) as number) : undefined,
    end_duration: params?.end_duration ? (unref(params?.end_duration) as number) : undefined,
    persons_count: params?.persons_count ? (unref(params?.persons_count) as number) : undefined,
    page: params?.page ? (unref(params?.page) as number) : undefined,
    tag__slugs: params?.tag__slugs ? (unref(params?.tag__slugs) as string) : undefined,
    sorting: params?.sorting ? (unref(params?.sorting) as string) : undefined,
    date_filter_mode: params?.date_filter_mode ? (unref(params?.date_filter_mode) as string) : undefined,
    bookable: params?.bookable !== undefined ? (unref(params?.bookable) as boolean) : undefined,
    start_time_interval: params?.start_time_interval ? (unref(params?.start_time_interval) as string) : undefined,
    comfort_type: params?.comfort_type ? (unref(params?.comfort_type) as string) : undefined,
    accommodation_type: params?.accommodation_type ? (unref(params?.accommodation_type) as string) : undefined,
    activity_level_category: params?.activity_level_category
      ? (unref(params?.activity_level_category) as string)
      : undefined,
    duration_interval: params?.duration_interval ? (unref(params?.duration_interval) as string) : undefined,
    price_format: params?.price_format ? (unref(params?.price_format) as string) : undefined,
    children_allowed: params?.children_allowed !== undefined ? (unref(params?.children_allowed) as boolean) : undefined,
    extended_movement_types: true,
  };
};
export type UseExperienceAggregationsOptions = UseExperienceAggregationsDisplayCurrencyOptions;

export const useExperienceAggregations = (
  params: ComputedRef<Params>,
  options: UseExperienceAggregationsOptions = {},
) => {
  const loaderStore = useLoaderStore();

  const { getExperienceAggregations } = useExperienceApi();
  const { hideUnlicensed } = useUnlicensedContentFilter();
  const currencyQueryParams = useAggregationDisplayCurrencyQueryParams(options);

  /** Явный снимок параметров в queryKey: иначе vue-query может не пересобрать ключ при мутации Pinia внутри исходного computed. */
  const aggregationQueryKeyParams = computed(() => ({
    ...normalizeParams(params.value),
    ...currencyQueryParams.value,
  }));

  const { data: experienceAggregationsData, isFetching: isExperienceAggregationsPending } = useQuery({
    queryKey: computed(() => ['experienceAggregations', aggregationQueryKeyParams.value]),
    queryFn: async () => {
      const response = await getExperienceAggregations({
        query: {
          ...normalizeParams(params.value),
          exclude_needs_license: hideUnlicensed.value,
          ...currencyQueryParams.value,
        },
      });

      if (response?.response?.status === 400) {
        loaderStore.is404 = true;
      }

      if (response?.data) {
        return createExperienceAggregationsFromResource(response.data);
      }
    },
    placeholderData: (prevData) => prevData ?? undefined,
    refetchOnWindowFocus: false,
  });

  const createComputedField = <T extends keyof IExperienceAggregations>(key: T) =>
    computed(() => experienceAggregationsData.value?.[key] ?? null);

  const processedMovementType = computed(() => {
    const movementTypeData = experienceAggregationsData.value?.movementType;

    if (!movementTypeData || typeof movementTypeData !== 'object') {
      return null;
    }

    const result: Partial<Record<TMovementType, number>> = {};

    for (const key in movementTypeData) {
      if (Object.prototype.hasOwnProperty.call(movementTypeData, key)) {
        const value = movementTypeData[key as keyof typeof movementTypeData];
        if (typeof value === 'number') {
          result[key as TMovementType] = value;
        }
      }
    }

    return Object.keys(result).length > 0 ? (result as Record<TMovementType, number>) : null;
  });

  return {
    tags: createComputedField('tags'),
    tourActivities: createComputedField('tourActivities'),
    tagsSet: createComputedField('tagsSet'),
    type: createComputedField('type'),
    customType: createComputedField('customType'),
    movementType: processedMovementType,
    price: createComputedField('price'),
    duration: createComputedField('duration'),
    date: createComputedField('date'),
    availability: createComputedField('availability'),

    isExperienceAggregationsPending,
  };
};
